import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Search, X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "cq_credit_analysis_prompt_dismissed";

/**
 * Pops up ONE time per session when the client is in the
 * "Credit Analysis" stage AND has not yet uploaded a credit report.
 * Non-blocking — the client can dismiss to keep browsing.
 */
export default function CreditAnalysisPrompt({ status }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (status !== "Credit Analysis") return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/funding/credit-report/me");
        if (cancelled) return;
        // Only prompt if the client has NEVER uploaded a report
        if (!data || (data.total ?? 0) === 0) {
          setOpen(true);
        }
      } catch {
        // On any error just don't nag the user
      }
    })();
    return () => { cancelled = true; };
  }, [status]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  const goUpload = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
    nav("/portal/funding?section=my_report");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-lg" data-testid="credit-analysis-prompt">
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-lg shadow-amber-200 mb-2">
            <Search size={26} strokeWidth={2.5} />
          </div>
          <DialogTitle className="text-center text-2xl font-display font-black text-slate-900">
            You're in the Credit Analysis stage
          </DialogTitle>
          <DialogDescription className="text-center text-slate-600 mt-1 text-base leading-relaxed">
            Want a stronger analysis and a Funding Center powered by your <b>actual numbers</b>?
            Upload your credit report PDF and our AI reads every tradeline, score, and inquiry in seconds.
          </DialogDescription>
        </DialogHeader>

        <ul className="text-sm text-slate-700 space-y-1.5 mt-2 mx-auto max-w-sm">
          <li className="flex items-start gap-2"><Sparkles size={14} className="text-amber-500 mt-0.5 shrink-0" /> Real card + LOC matches from your live profile</li>
          <li className="flex items-start gap-2"><Sparkles size={14} className="text-amber-500 mt-0.5 shrink-0" /> Auto-populates your Funding Simulator</li>
          <li className="flex items-start gap-2"><Sparkles size={14} className="text-amber-500 mt-0.5 shrink-0" /> Tracks score deltas between reports</li>
        </ul>

        <DialogFooter className="mt-4 sm:justify-center gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={dismiss}
            className="border-slate-200"
            data-testid="credit-analysis-skip"
          >
            <X size={14} className="mr-1" /> Skip for now
          </Button>
          <Button
            onClick={goUpload}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            data-testid="credit-analysis-upload"
          >
            Upload My Report <ArrowRight size={14} className="ml-1" />
          </Button>
        </DialogFooter>
        <p className="text-center text-[11px] text-slate-400 mt-1">
          Optional. Skipping won't slow down your case.
        </p>
      </DialogContent>
    </Dialog>
  );
}
